import { DetailsPopup } from "modules/common/components/form/DetailsPopup";
import { Block } from "modules/common/components/page/Block";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { Search } from "modules/common/components/form/Search";
import { EMPTY_OBJECT_ID } from "modules/common/constants";
import { routes } from "modules/common/routes";
import { ProjectPortfolioDetails } from "modules/dictionaries/project-portfolios/components/details/ProjectPortfolioDetails";
import { ProjectPortfoliosList } from "modules/dictionaries/project-portfolios/components/list/ProjectPortfoliosList";
import { ProjectPortfolioSnapshotType } from "modules/dictionaries/project-portfolios/models/project-portfolio";
import { ProjectPortfoliosStoreType } from "modules/dictionaries/project-portfolios/models/project-portfolio-store";
import { CompanyBankDetailsStoreType } from "modules/dictionaries/bank-details/models/bank-details-store";
import func from "modules/dictionaries/functionalities";
import { menuItems } from "modules/root/components/menu/MainMenu";
import { can } from "modules/session/auth/access";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { matchRoute } from "modules/common/services/routing";
import { WhenPath } from "modules/common/components/routing/WhenPath";
import { AccountsPanel } from "./AccountsPanel";
import styles from "./OrganisationsPage.module.scss";
import { useStableNavigate } from "modules/common/components/routing/useStableNavigate";

const menu = menuItems();

const getSelectedOrganisationId = (pathname: string) => {
  const match = matchRoute<{ portfolioId: string }>(pathname, routes.spending.organisations.details());

  return match && match.params ? match.params.portfolioId : "";
};

/**
 * Организации (наши юр. лица — они же исполнители договоров) и их счета на оплату.
 * Устроена как «Структура организации»: слева справочник, справа — принадлежащие записи.
 */
export const OrganisationsPage = observer(({ store, bankDetails, access }: OrganisationsPageProps) => {
  const navigate = useStableNavigate();
  const { pathname } = useLocation();
  const organisationId = getSelectedOrganisationId(pathname);
  const [selected, setSelected] = useState<ProjectPortfolioSnapshotType | null>(null);

  useEffect(() => {
    document.title = menu.spending.nested.organisations.label;

    store.list.load();
  }, [store.list]);

  // карточка следует за адресом: открыли другую организацию — загружаем её
  useEffect(() => {
    store.details.load(organisationId);
  }, [store.details, organisationId]);

  // пока ничего не выбрано — показываем счета первой организации
  useEffect(() => {
    if (selected || !store.list.data.length) {
      return;
    }

    setSelected(store.list.data[0]);
  }, [selected, store.list.data]);

  const openOrganisation = useCallback(
    (id: string = "") => {
      const url = id ? routes.spending.organisations.details(id) : routes.spending.organisations.path;

      navigate(url);
    },
    [navigate]
  );

  // одиночный клик по строке выбирает организацию, счета которой показываем справа
  const onOrganisationClicked = useCallback(
    (id: string = "") => {
      const row = store.list.data.find((item) => item.id === id) ?? null;

      setSelected(row);
    },
    [store.list]
  );

  const onOrganisationAdd = useCallback(() => openOrganisation(EMPTY_OBJECT_ID), [openOrganisation]);

  const onOrganisationSaved = useCallback(() => {
    openOrganisation("");
    store.list.load();
  }, [openOrganisation, store.list]);

  const onOrganisationRemoved = useCallback(() => {
    setSelected(null);
    store.list.load();
    navigate(routes.spending.organisations.path);
  }, [navigate, store.list]);

  const canChange = can(func.BANKDETAILS_EDIT, access);

  return (
    <div className={styles.container}>
      <Block columned={true} className={styles.page}>
        <WhenPath path={routes.spending.organisations.details()}>
          <DetailsPopup title="Организация" onCancel={openOrganisation}>
            <ProjectPortfolioDetails
              projectPortfolio={store.details}
              employers={store.list.employers}
              onSaved={onOrganisationSaved}
              onRemoved={onOrganisationRemoved}
              canChange={canChange}
            />
          </DetailsPopup>
        </WhenPath>

        <span className={styles.topSpan}>Организации</span>

        {canChange && (
          <PlanrButton
            type="secondary"
            size="small"
            icon="general-plus-big"
            onClick={onOrganisationAdd}
            style={{
              width: "230px",
              marginBottom: "32px",
              borderRadius: "120px",
            }}
          >
            Организация
          </PlanrButton>
        )}

        <div className={styles.right}>
          <Search query={store.list.query} onSearch={store.list.setQuery} />
        </div>

        <ProjectPortfoliosList
          store={store.list}
          onSelected={openOrganisation}
          onClicked={onOrganisationClicked}
          onRemoved={onOrganisationRemoved}
        />
      </Block>

      <AccountsPanel store={bankDetails} organisation={selected} canChange={canChange} />
    </div>
  );
});

interface OrganisationsPageProps {
  store: ProjectPortfoliosStoreType;
  bankDetails: CompanyBankDetailsStoreType;
  access: string[];
}
